import { mdiPlay, mdiArrowRight, mdiLock } from "@mdi/js";
import Icon from "@mdi/react";
import { BetQLSportItem } from "@rotoql/common-services";
import pluralize from "pluralize";
import React, { useContext } from "react";
import { COMP_GREEN, MAIN_BLUE } from "src/colors";
import TeamLogo from "src/components/Team/TeamLogo";
import { capitalize } from "src/services/DisplayService";
import { SubscriptionContext } from "src/services/SubscriptionService";

interface DrawerProps {
  game: any;
  currentSportObj: BetQLSportItem;
  periodFilter: string;
}

const RenderSelectedTeam = ({
  isDraw,
  team,
  currentSportObj,
}: {
  isDraw: boolean;
  team: any;
  currentSportObj: BetQLSportItem;
}) => {
  if (isDraw) {
    return (
      <article className="selected-team">
        <Icon path={mdiPlay} size="16px" color={COMP_GREEN} />
        <div style={{ marginLeft: 15 }}>Draw</div>
      </article>
    );
  }

  return (
    <article className="selected-team">
      <Icon path={mdiPlay} size="16px" color={COMP_GREEN} />

      <TeamLogo
        leagueName={currentSportObj.imagePath}
        teamAbbreviation={currentSportObj.isTennis ? team.country : team.preferredAbbreviation}
        size={26}
      />
      <span className="trend-text">{team.preferredAbbreviation}</span>
    </article>
  );
};

const RenderUpgradeButton = ({
  classname,
  game,
  currentSportObj,
  redirectToPricing,
}: {
  classname: string;
  game: any;
  currentSportObj: BetQLSportItem;
  redirectToPricing: any;
}) => {
  return (
    <button
      className={classname || ""}
      onClick={() =>
        redirectToPricing(
          {
            screen: "GamesView",
            page: "Games Table",
            sport: currentSportObj.name,
            source: "Top Trends Drawer",
            awayTeam: game.awayTeam.fullName,
            homeTeam: game.homeTeam.fullName,
            gameId: game.id,
          },
          "Games Table Top Trends Drawer"
        )
      }
    >
      <Icon path={mdiLock} size="20px" color="#004198" />
      <span className="paywall-text">Upgrade</span>
    </button>
  );
};

const TrendModelDrawer = ({ game, currentSportObj, periodFilter }: DrawerProps) => {
  const { hasSubscriptionAccess, redirectToPricing } = useContext(SubscriptionContext);
  const hasAccess = hasSubscriptionAccess(currentSportObj.key) || game.freeGame;
  const isTotalBet = periodFilter.includes("total");

  const {
    awayTeam,
    homeTeam,
    trendsPick: {
      betqlPick,
      fadeThePublicPick,
      proSharpPick,
      communityPick,
      awayCount,
      homeCount,
      drawCount,
      favoredTeam,
    },
  } = game;

  const models = [
    {
      label: "BetQL Model",
      selectedTeam: betqlPick,
    },
    {
      label: "BetQL Community",
      selectedTeam: communityPick,
    },
    {
      label: "Sharp Betting",
      selectedTeam: proSharpPick,
    },
    {
      label: "Fade the Public",
      selectedTeam: fadeThePublicPick,
    },
  ];

  const getDescription = () => {
    const isDraw = favoredTeam === "draw";
    // if its a Draw
    if (isDraw) {
      return (
        <p>
          There {pluralize("is", drawCount)} {pluralize("trend", drawCount, true)} favoring a <b>Draw</b> for this bet
          type.
        </p>
      );
    }
    // if no favoredTeam, then its draw then we use any team is fine.
    const isHome = favoredTeam === "home";
    const selectedTeam = isHome ? homeTeam : awayTeam;
    // if there is no favored team, isHome is false then we will by default use away
    const selectedTeamCount = favoredTeam
      ? game.trendsPick[`${favoredTeam}Count`]
      : Math.max(awayCount, drawCount, homeCount);

    const prefix = pluralize("is", selectedTeamCount);
    const displayText = pluralize("trend", selectedTeamCount, true);

    // no favoredTeam = draw, just any team and see if it's 0
    if (!favoredTeam && !selectedTeamCount) {
      return <p>Neither side has a trend that applies yet. Check back closer to game start.</p>;
    }

    // no favoredTeam = draw if there is at least one trend
    if (!favoredTeam && selectedTeamCount) {
      return (
        <p>
          There {prefix} {displayText} favoring both sides for this bet type.
        </p>
      );
    }

    const selectedTeamName = currentSportObj.isCollege ? selectedTeam.fullName : selectedTeam.lastName;
    const overOrUnder = isHome ? "Under" : "Over";
    const displaySide = isTotalBet ? overOrUnder : selectedTeamName;

    return (
      <p>
        There {prefix} {displayText} favoring {!currentSportObj.isCollege ? "the " : " "}
        <span className="team-bold">{displaySide}</span> for this bet type.
      </p>
    );
  };

  const homeTeamName = currentSportObj.isCollege ? homeTeam.fullName : homeTeam.lastName;
  const awayTeamName = currentSportObj.isCollege ? awayTeam.fullName : awayTeam.lastName;

  return (
    <div className="trend-models-drawer-best-line">
      <div className="trend-drawer-header">
        {hasAccess ? (
          <>
            {getDescription()}
            <p>Click the Full Summary below to see more analysis</p>
          </>
        ) : (
          <p className="upgrade-to-see-team-text">
            Upgrade to see if the {awayTeamName} or {homeTeamName} are recommended across models for this bet
          </p>
        )}
      </div>

      {models.map(({ label, selectedTeam }) => {
        if (!selectedTeam) {
          return null;
        }

        const team = game[`${selectedTeam}Team`];
        const line = game[`current${periodFilter}${capitalize(selectedTeam)}Display`];
        const classNameForColumn = hasAccess && !isTotalBet ? "three-column" : "two-column";
        const isHome = selectedTeam === "home";
        const isDraw = selectedTeam === "draw";
        const prefix = isHome ? "U " : "O ";

        return (
          <div className={`trend-model ${classNameForColumn}`}>
            <span className="trend-model-type">{label}</span>
            {hasAccess ? (
              <>
                {!isTotalBet ? (
                  <RenderSelectedTeam team={team} isDraw={isDraw} currentSportObj={currentSportObj} />
                ) : null}
                <span className="trend-text">
                  {isTotalBet ? prefix : null} {line}
                </span>
              </>
            ) : (
              <RenderUpgradeButton
                classname="trend-line-paywall"
                currentSportObj={currentSportObj}
                game={game}
                redirectToPricing={redirectToPricing}
              />
            )}
          </div>
        );
      })}

      {hasAccess ? (
        <a className="trend-drawer-footer" href={`/${currentSportObj.url}/game-predictions/${game.slugId}`}>
          View Full Summary Here
          <Icon path={mdiArrowRight} size="16px" color={MAIN_BLUE} style={{ marginLeft: 5 }} />
        </a>
      ) : (
        <RenderUpgradeButton
          classname="trend-drawer-footer-paywall"
          currentSportObj={currentSportObj}
          game={game}
          redirectToPricing={redirectToPricing}
        />
      )}
    </div>
  );
};

export default TrendModelDrawer;
